import { defineComponent, ref, watch, PropType, onMounted, onUnmounted, nextTick } from "vue";
import { getPrefixCls } from "@vue-admin-core/utils/const";
import emitter from "./mitt";
import type { IDomEditor } from "@wangeditor/core";
import { VariableElement } from "./types";
import { useElementHover, useScroll } from "@vueuse/core";

const prefixCls = getPrefixCls("msg-editor");

export default defineComponent({
  name: "MsgEditorPopper",
  props: {
    visible: {
      type: Boolean
    },
    position: {
      type: Object
    },
    options: {
      type: Array as PropType<{ label: string; value: string }[]>,
      default: () => []
    },
    editor: {
      type: Object as PropType<IDomEditor>
    },
    value: {
      type: String
    }
  },
  setup(props, { attrs, expose }) {
    // const virtualEl = ref();
    const floating = ref();
    const arrowElement = ref();
    const hoverItem = ref();
    const selectValue = ref();
    const popperList = ref<HTMLElement | null>(null);

    const { y } = useScroll(popperList);
    const isHovered = useElementHover(popperList);

    watch(
      () => [props.position, props.visible],
      () => {
        if (!floating.value) return;
        if (props.visible && props.position) {
          Object.assign(floating.value.style, {
            left: `${props.position.value.left - (props.position.value.isRight ? 10 : 20)}px`,
            top: `${props.position.value.top}px`,
            display: "block"
          });
          hoverItem.value = props.options[0];
        } else {
          Object.assign(floating.value.style, {
            display: "none"
          });
        }
      },
      { deep: true, immediate: true }
    );

    watch(
      () => props.value,
      (value) => {
        selectValue.value = value;
      }
    );

    const select = () => {
      if (!props.editor || !hoverItem.value) return;
      props.editor.restoreSelection();
      props.editor.deleteBackward("character");
      // 插入 variable 节点
      const variableNode: VariableElement = {
        type: "variable",
        value: hoverItem.value.value,
        label: hoverItem.value.label,
        children: [{ text: "" }]
      };
      props.editor.insertNode(variableNode);
      // 光标移动一位
      props.editor.move(1);
    };

    const handleClick = (e: MouseEvent, item: any) => {
      hoverItem.value = item;
      nextTick(() => {
        select();
      });
    };

    onMounted(() => {
      emitter.on("ArrowDown", () => {
        if (hoverItem.value) {
          let index = props.options.findIndex((item) => item.value === hoverItem.value.value);
          if (index === props.options.length - 1) {
            index = -1;
          }
          hoverItem.value = props.options[index + 1];
        }
      });
      emitter.on("ArrowUp", () => {
        if (hoverItem.value) {
          let index = props.options.findIndex((item) => item.value === hoverItem.value.value);
          if (index === 0) {
            index = props.options.length;
          }
          hoverItem.value = props.options[index - 1];
        }
      });
      emitter.on("Enter", () => {
        select();
      });
    });

    onUnmounted(() => {
      emitter.all.clear();
    });

    expose({
      setValue: (value: any) => {
        if (value) {
          hoverItem.value = value;
        }
      },
      target: floating
    });

    watch(
      () => hoverItem.value,
      async () => {
        if (isHovered.value) return;
        await nextTick();
        const el = popperList.value?.querySelector(".is-hovering") as any;
        y.value = el?.offsetTop;
      }
    );

    watch(
      () => selectValue.value,
      async (value) => {
        if (isHovered.value || !value) return;
        await nextTick();
        const el = popperList.value?.querySelector(".is-selected") as any;
        y.value = el?.offsetTop;
      }
    );

    return () => {
      return (
        <div {...attrs} ref={floating}>
          <div ref={popperList} class={`${prefixCls}__popper__list`}>
            {props.options.map((item) => (
              <div
                class={[
                  `${prefixCls}__popper__item`,
                  { "is-hovering": hoverItem.value?.value === item.value },
                  { "is-selected": selectValue.value === item.value }
                ]}
                onMouseenter={() => (hoverItem.value = item)}
                onClick={(e: any) => handleClick(e, item)}
              >
                {item.label}
              </div>
            ))}
          </div>
          <div class={`${prefixCls}__arrow`} ref={arrowElement}></div>
        </div>
      );
    };
  }
});
